import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Menu, 
  X, 
  Shield, 
  Activity, 
  Database, 
  Users, 
  Phone, 
  Mail, 
  MapPin,
  CheckCircle,
  ArrowRight,
  Star
} from 'lucide-react';
import './App.css';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="App">
      {/* Header/Navigation */}
      <header className="header">
        <nav className="nav-container">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="logo"
          >
            <h2>Obatron Healthcare</h2>
            <span>Medical Device System</span>
          </motion.div>

          <div className="nav-desktop">
            <a href="#home">Home</a>
            <a href="#features">Features</a>
            <a href="#about">About</a>
            <a href="#contact">Contact</a>
          </div>

          <button className="menu-toggle" onClick={toggleMenu}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {isMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="nav-mobile"
            >
              <a href="#home" onClick={() => setIsMenuOpen(false)}>Home</a>
              <a href="#features" onClick={() => setIsMenuOpen(false)}>Features</a>
              <a href="#about" onClick={() => setIsMenuOpen(false)}>About</a>
              <a href="#contact" onClick={() => setIsMenuOpen(false)}>Contact</a>
            </motion.div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-container">
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <h1>Medical Device System</h1>
            <p className="hero-subtitle">
              Advanced healthcare technology solutions for modern medical facilities
            </p>
            <p className="hero-description">
              Complete medical device management, monitoring, and optimization system 
              designed to enhance patient care and operational efficiency.
            </p>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="cta-button"
            >
              Explore Features <ArrowRight size={20} />
            </motion.button>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-visual"
          >
            <div className="hero-card">
              <Activity size={60} className="hero-icon" />
              <h3>Smart Monitoring</h3>
              <p>Real-time device tracking and health monitoring</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>System Features</h2>
            <p>Comprehensive medical device management capabilities</p>
          </motion.div>

          <div className="features-grid">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="feature-card"
            >
              <Shield className="feature-icon" />
              <h3>Security & Compliance</h3>
              <p>HIPAA compliant with advanced security protocols to protect patient data and ensure regulatory compliance.</p>
              <ul>
                <li><CheckCircle size={16} /> End-to-end encryption</li>
                <li><CheckCircle size={16} /> Audit trails</li>
                <li><CheckCircle size={16} /> Access controls</li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="feature-card"
            >
              <Activity className="feature-icon" />
              <h3>Real-time Monitoring</h3>
              <p>Continuous monitoring of medical devices with instant alerts and notifications for optimal performance.</p>
              <ul>
                <li><CheckCircle size={16} /> Live device status</li>
                <li><CheckCircle size={16} /> Performance analytics</li>
                <li><CheckCircle size={16} /> Predictive maintenance</li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="feature-card"
            >
              <Database className="feature-icon" />
              <h3>Data Management</h3>
              <p>Centralized database for all medical device information, maintenance records, and usage analytics.</p>
              <ul>
                <li><CheckCircle size={16} /> Cloud storage</li>
                <li><CheckCircle size={16} /> Data backup & recovery</li>
                <li><CheckCircle size={16} /> Advanced reporting</li>
              </ul>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="feature-card"
            >
              <Users className="feature-icon" />
              <h3>User Management</h3>
              <p>Multi-level user access control with role-based permissions for healthcare staff and administrators.</p>
              <ul>
                <li><CheckCircle size={16} /> Role-based access</li>
                <li><CheckCircle size={16} /> Staff training modules</li>
                <li><CheckCircle size={16} /> Usage tracking</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="about-text"
            >
              <h2>About Obatron Healthcare</h2>
              <p className="about-intro">
                Established in 2022, Obatron Healthcare Co., Ltd. is a leading provider of 
                modern healthcare technology solutions and medical facility systems.
              </p>
              
              <div className="about-details">
                <h3>Our Expertise</h3>
                <ul className="expertise-list">
                  <li><CheckCircle size={16} /> Air conditioning systems installation and maintenance</li>
                  <li><CheckCircle size={16} /> Complete HVAC solutions for medical facilities</li>
                  <li><CheckCircle size={16} /> Interior design and renovation</li>
                  <li><CheckCircle size={16} /> Electrical systems for healthcare environments</li>
                  <li><CheckCircle size={16} /> Building automation systems</li>
                  <li><CheckCircle size={16} /> Medical device management software</li>
                </ul>

                <h3>Our Mission</h3>
                <p>
                  We focus on developing modern technology and engineering solutions to meet our 
                  customers' needs while continuously developing quality personnel for excellence 
                  in quality, cost, time, and service delivery.
                </p>

                <div className="benefits">
                  <h3>Employee Benefits</h3>
                  <div className="benefits-grid">
                    <div className="benefit-item">
                      <Star className="benefit-icon" />
                      <span>Competitive salary</span>
                    </div>
                    <div className="benefit-item">
                      <Star className="benefit-icon" />
                      <span>Annual bonus based on performance</span>
                    </div>
                    <div className="benefit-item">
                      <Star className="benefit-icon" />
                      <span>Annual leave and vacation days</span>
                    </div>
                    <div className="benefit-item">
                      <Star className="benefit-icon" />
                      <span>Social security benefits</span>
                    </div>
                    <div className="benefit-item">
                      <Star className="benefit-icon" />
                      <span>Professional training programs</span>
                    </div>
                    <div className="benefit-item">
                      <Star className="benefit-icon" />
                      <span>Performance-based salary increases</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="about-stats"
            >
              <div className="stat-card">
                <h3>2022</h3>
                <p>Established</p>
              </div>
              <div className="stat-card">
                <h3>24/7</h3>
                <p>System Monitoring</p>
              </div>
              <div className="stat-card">
                <h3>100+</h3>
                <p>Healthcare Facilities</p>
              </div>
              <div className="stat-card">
                <h3>99.9%</h3>
                <p>System Uptime</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2>Contact Us</h2>
            <p>Get in touch with our team for more information</p>
          </motion.div>

          <div className="contact-content">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="contact-info"
            >
              <h3>Obatron Healthcare Co., Ltd.</h3>
              
              <div className="contact-item">
                <MapPin className="contact-icon" />
                <div>
                  <p><strong>Address:</strong></p>
                  <p>63/71 Golden City Village</p>
                  <p>Chaeng Watthana-Muang Thong, Moo 5, Soi 10</p>
                  <p>Tiwanon Road, Ban Mai Sub-district</p>
                  <p>Pak Kret District, Nonthaburi 11120</p>
                </div>
              </div>

              <div className="contact-item">
                <Phone className="contact-icon" />
                <div>
                  <p><strong>Phone:</strong></p>
                  <p>+66 (0) XX XXX XXXX</p>
                </div>
              </div>

              <div className="contact-item">
                <Mail className="contact-icon" />
                <div>
                  <p><strong>Email:</strong></p>
                  <p>info@obatron.co.th</p>
                </div>
              </div>

              <div className="working-hours">
                <h4>Working Hours</h4>
                <p>Monday - Saturday: 8:00 AM - 5:00 PM</p>
              </div>

              <div className="transportation">
                <h4>Transportation</h4>
                <ul>
                  <li>Bus routes: 33, 367, 359</li>
                  <li>Songthaew routes: 1029, 1027</li>
                  <li>Located in Golden City Village next to Military School</li>
                </ul>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="contact-form"
            >
              <form>
                <div className="form-group">
                  <input type="text" placeholder="Your Name" required />
                </div>
                <div className="form-group">
                  <input type="email" placeholder="Your Email" required />
                </div>
                <div className="form-group">
                  <input type="tel" placeholder="Your Phone" />
                </div>
                <div className="form-group">
                  <select>
                    <option value="">Select Service</option>
                    <option value="mds">Medical Device System</option>
                    <option value="hvac">HVAC Systems</option>
                    <option value="electrical">Electrical Systems</option>
                    <option value="interior">Interior Design</option>
                    <option value="other">Other Services</option>
                  </select>
                </div>
                <div className="form-group">
                  <textarea placeholder="Your Message" rows="5" required></textarea>
                </div>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit" 
                  className="submit-button"
                >
                  Send Message
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Obatron Healthcare</h3>
              <p>Advanced healthcare technology solutions for modern medical facilities.</p>
            </div>
            <div className="footer-section">
              <h4>Services</h4>
              <ul>
                <li>Medical Device System</li>
                <li>HVAC Solutions</li>
                <li>Electrical Systems</li>
                <li>Interior Design</li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#features">Features</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 Obatron Healthcare Co., Ltd. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
